import { HttpErrorResponse, type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { ENDPOINTS, NO_REFRESH_ENDPOINTS } from '@core/api/api-endpoints';
import { AuthService } from '@core/auth/auth.service';

import { endpointOf, isApiUrl, isAuthEndpointUrl } from './api-url.util';

/**
 * SECOND in the chain, and that position is load-bearing: it sits inside the
 * loading interceptor (so one spinner brackets the retry) and outside the
 * error interceptor (so the retried request, carrying the REFRESHED token, is
 * what the error interceptor observes - a 401 that succeeded on retry must
 * never toast).
 *
 * Responsibilities, in order:
 *  1. Bearer header, for our API URLs only.
 *  2. withCredentials, for api/Auth/* only.
 *  3. On 401: one single-flight refresh, one retry, then force logout.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Not our API: no credential of ours has any business on this request.
  if (!isApiUrl(req.url)) return next(req);

  const auth = inject(AuthService);
  const endpoint = endpointOf(req.url);
  const authRoute = isAuthEndpointUrl(req.url);
  const isRefreshCall = endpoint === ENDPOINTS.auth.refreshToken;

  return next(decorate(req, auth.accessToken(), authRoute, isRefreshCall)).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      // A 401 from Login / RequestOtp / VerifyOtp is a CREDENTIAL failure and
      // from RefreshToken is a dead session. Refreshing in answer to any of
      // them turns one mistyped password into a refresh-and-retry loop.
      if (NO_REFRESH_ENDPOINTS.includes(endpoint)) {
        if (isRefreshCall) auth.forceLogout();
        return throwError(() => error);
      }

      return auth.refresh().pipe(
        // The refresh is single-flight in AuthService, so N concurrent 401s
        // wait on ONE network call and then all retry with the same new token.
        switchMap((result) => next(decorate(req, result.accessToken, authRoute, false))),
        catchError(() => {
          // Either the refresh failed or the retry 401'd as well. One retry is
          // the budget; a second would be a loop against a revoked session.
          auth.forceLogout();
          return throwError(() => error);
        }),
      );
    }),
  );
};

/**
 * Applies the two credentials to a request.
 *
 * The refresh call deliberately gets NO Authorization header: it authenticates
 * with the HttpOnly cookie, and the only token we could attach is the expired
 * one that caused the 401 in the first place.
 */
function decorate(
  req: HttpRequest<unknown>,
  token: string | null,
  authRoute: boolean,
  isRefreshCall: boolean,
): HttpRequest<unknown> {
  let out = req;

  if (authRoute) {
    out = out.clone({ withCredentials: true });
  }

  if (token && !isRefreshCall) {
    out = out.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return out;
}
